/**
 * useSaveHandler -- Reusable hook for save/delete operations with feedback.
 *
 * Encapsulates the isSaving / saveMessage / saveError pattern used across
 * all CMS editor pages and ResumeEditorPage. Reduces ~15 lines of duplicated
 * boilerplate per editor component to a single hook call.
 */
import { useState, useCallback, useRef } from 'react';

interface UseSaveHandlerOptions {
  /** Returns the current error string from the Zustand store (called after each action). */
  getStoreError: () => string | null;
  /** Duration in ms to show the success message. Default: 3000. */
  successTimeout?: number;
}

interface UseSaveHandlerReturn {
  /** Whether an action is currently in flight. */
  isSaving: boolean;
  /** Success message shown briefly after a successful operation. */
  saveMessage: string | null;
  /** Error message from the store or a caught exception. */
  saveError: string | null;
  /**
   * Execute an async store action with save feedback.
   *
   * Resets messages, sets isSaving, runs the action, then checks the store
   * for errors. On success, shows a timed success message and calls onSuccess.
   */
  execute: (
    action: () => Promise<unknown>,
    opts?: { onSuccess?: () => void; successMessage?: string },
  ) => Promise<void>;
}

export function useSaveHandler(options: UseSaveHandlerOptions): UseSaveHandlerReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Keep options in a ref to avoid stale closures without adding to useCallback deps.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const execute: UseSaveHandlerReturn['execute'] = useCallback(
    async (action, opts) => {
      setIsSaving(true);
      setSaveMessage(null);
      setSaveError(null);

      await action();

      setIsSaving(false);
      const storeErr = optionsRef.current.getStoreError();
      if (storeErr) {
        setSaveError(storeErr);
      } else {
        const msg = opts?.successMessage ?? 'Saved successfully';
        setSaveMessage(msg);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(
          () => setSaveMessage(null),
          optionsRef.current.successTimeout ?? 3000,
        );
        opts?.onSuccess?.();
      }
    },
    [],
  );

  return { isSaving, saveMessage, saveError, execute };
}
